import ExportarCsv from '@/global/components/DataGrid/components/CustomGridToolBar/ExportarCsv'
import {
    GridMenuButton,
    GridMenuItem,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { PeriodoPontoFactory } from '@acme/common'
import { TRelatorioAusencias } from '@acme/core'
import { Button } from '@/global/components/Button'
import Menu from 'global/components/MenuV2'
import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
import ContentGuide from 'global/guides/ContentGuide'
import { useUrlState } from 'hooks'
import { hasPermission } from 'hooks/useBackendMenu'
import { FileUserIcon, PlusIcon, UploadIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { ajustes, hasPermissionToAnyAfastamento } from 'service/api'
import { PaginatedResponse } from 'service/api/types'
import { toast } from 'sonner'
import { useStore } from 'store'
import { columns } from 'templates/ManutencaoDoPonto/Ausencias/columns'

export default function PaginaAusencias() {
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
    const empresa = useStore(state => state.company)
    const empresaId = empresa?.id ?? -1
    const diaInicioPonto = empresa?.diaInicioPonto ?? 1
    const trocasInicioPonto = useMemo(
        () => empresa?.TrocaInicioPonto ?? [],
        [empresa?.TrocaInicioPonto],
    )

    const [competencia] = PeriodoPontoFactory.fromPeriodo(
        dataInicial,
        dataFinal,
    ).getCompetencias({
        diaInicioPonto,
        trocasInicioPonto,
    })

    const [{ page, perPage }, setUrlPagination] = useUrlState({
        page: 1,
        perPage: 25,
    })

    const { data, isLoading } = ajustes.relatorioAusencias.useFetch<
        PaginatedResponse<TRelatorioAusencias>
    >({
        keepPreviousData: true,
        queryParams: {
            dataInicial,
            dataFinal,
            empresaId,
            page,
            perPage,
        },
        enabled: empresaId !== -1,
    })

    const useFilter = useFilterLocaisCentroStore()
    const rows = (data?.data ?? []).filter(useFilter)

    const createMenuItems = [
        hasPermission(['create', 'Abonos']) && (
            <Menu.Item href={`abono/adicionar`} key="abono">
                Abono
            </Menu.Item>
        ),
        hasPermissionToAnyAfastamento('create') && (
            <Menu.Item href={`afastamento/adicionar`} key="afastamento">
                Afastamento
            </Menu.Item>
        ),
        hasPermission(['create', 'CompensaçãoDeFeriados']) && (
            <Menu.Item
                href={`compensacao-feriado/adicionar`}
                key="compensacao-feriado"
            >
                Compensação de feriado
            </Menu.Item>
        ),
        hasPermission(['create', 'CompensaçãoDeFolgas']) && (
            <Menu.Item
                href={`compensacao-folga/adicionar`}
                key="compensacao-folga"
            >
                Compensação de folga
            </Menu.Item>
        ),
        ...(hasPermission(['create', 'FaltasJustificadasOuInjustificadas'])
            ? [
                  <Menu.Item
                      href={`falta-injustificada/adicionar`}
                      key="falta-injustificada"
                  >
                      Falta injustificada
                  </Menu.Item>,
                  <Menu.Item
                      href={`falta-justificada/adicionar`}
                      key="falta-justificada"
                  >
                      Falta justificada
                  </Menu.Item>,
              ]
            : []),
    ].filter(Boolean)

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={['Manutenção do ponto', 'Ausências']}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SP.Root
                            onChange={setPeriodo}
                            value={[dataInicial, dataFinal]}
                            width={320}
                        >
                            <SP.DateSelector />
                            <SP.Buttons />
                        </SP.Root>
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    serverSide
                    loading={isLoading}
                    totalRows={data?.meta?.total}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                        setUrlPagination({
                            page: pageIndex + 1,
                            perPage: pageSize,
                        })
                    }}
                    defaultPageSize={perPage}
                    ToolbarProps={{
                        hideExportButton: true,
                    }}
                    components={{
                        Toolbar: () => (
                            <>
                                <GridMenuButton
                                    startIcon={<UploadIcon />}
                                    label="Exportar"
                                >
                                    <GridMenuItem
                                        startIcon={
                                            <FileUserIcon className="mr-2" />
                                        }
                                        onClick={() => {
                                            if (!rows?.length)
                                                return toast.warning(
                                                    'Não há dados para exportar',
                                                )
                                            else {
                                                toast.info(
                                                    'Seu pdf está sendo gerado.',
                                                )
                                                ajustes.relatorioAusencias
                                                    .pdf({
                                                        data: rows.map(
                                                            item => item.id,
                                                        ),
                                                        queryParam: {
                                                            dataInicial,
                                                            dataFinal,
                                                            empresaId,
                                                        },
                                                    })
                                                    .catch(e => {
                                                        toast.error(
                                                            'Erro ao gerar pdf. ' +
                                                                e.response.data
                                                                    .message,
                                                        )
                                                        console.error(e)
                                                    })
                                            }
                                        }}
                                        label="Exportar PDF"
                                    />
                                    <ExportarCsv
                                        mi
                                        fileName="exportacao-ausencias"
                                    />
                                </GridMenuButton>
                                <div style={{ flex: 1 }} />
                                {!!createMenuItems.length && (
                                    <div>
                                        <Menu.Root arrow>
                                            <Menu.Trigger>
                                                <Button
                                                    startIcon={<PlusIcon />}
                                                    variant="contained"
                                                >
                                                    Adicionar
                                                </Button>
                                            </Menu.Trigger>
                                            <Menu.List>
                                                {createMenuItems}
                                            </Menu.List>
                                        </Menu.Root>
                                    </div>
                                )}
                            </>
                        ),
                    }}
                    columns={columns}
                    rows={rows}
                />
            </ContentGuide.Card>
            <Outlet context={{ data: data?.data ?? [], competencia }} />
        </ContentGuide>
    )
}

export function useAusenciasOutletContext() {
    return useOutletContext<{
        data?: TRelatorioAusencias[]
        competencia: string
    }>()
}
